/**
 * Strike-Level Fresh OI Flow Terminal (Option Indicator Phase C —
 * /option-indicator/strike-charts). Sibling page to OIChartsPage (Phase
 * B), never modifying it.
 *
 * Ports oi_server.py's `/strike-charts` matrix: per-strike fresh call/put
 * OI accumulation (since the trade date's first captured bar), banded
 * around the day's opening spot. Every field here is a direct replay of
 * `oi_raw_option_chain` (see `strike_chart_history.py`'s module
 * docstring) — there are NO disclosed gaps for this page, unlike Phase B.
 *
 * Deliberate difference from the original app (disclosed in the info
 * banner below, not hidden): TekInvest's live collector polls every
 * `SCHEDULER_INTERVAL_MINUTES` (5 min in production) vs oi_server.py's
 * ~3.5 min loop, so the timeline resolution is coarser. Same field
 * semantics either way.
 */
import { indexSectionProps, useAnalystIndexView } from "@/context/AnalystIndexViewContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import oiStrikeChartsService from "@/services/oiStrikeChartsService";
import type { StrikeChartHistoryResponse, StrikeChartPoint } from "@/types";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Legend, ChartTooltip);

type IndexKey = "NIFTY" | "BANKNIFTY";

// Same big/small spike thresholds as oi_server.py's /strike-charts terminal
// (FP_BIG/FC_BIG/FP_SM/FC_SM), applied identically to both indices.
const FP_BIG = 50;
const FC_BIG = 50;
const FP_SM = 20;
const FC_SM = 20;

// Matches oi_server.py's nfIntervals/bnIntervals step (anchor, anchor-50,
// anchor+50 for Nifty; anchor, anchor-100, anchor+100 for BankNifty).
const STEP: Record<IndexKey, number> = { NIFTY: 50, BANKNIFTY: 100 };

interface TimeframeOption {
  label: string;
  minutes: number | null;
}
const TIMEFRAME_OPTIONS: TimeframeOption[] = [
  { label: "10 min", minutes: 10 },
  { label: "15 min", minutes: 15 },
  { label: "30 min", minutes: 30 },
  { label: "60 min", minutes: 60 },
  { label: "90 min", minutes: 90 },
  { label: "Full day", minutes: null },
];
const DEFAULT_TIMEFRAME_LABEL = "Full day";

function parseTsToMinutes(ts: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(ts);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

function filterByTimeframe(
  points: StrikeChartPoint[] | undefined,
  minutes: number | null,
): StrikeChartPoint[] {
  const pts = points ?? [];
  if (minutes === null || pts.length === 0) return pts;
  const lastMinutes = parseTsToMinutes(pts[pts.length - 1].ts);
  if (lastMinutes === null) return pts;
  return pts.filter((p) => {
    const t = parseTsToMinutes(p.ts);
    return t !== null && lastMinutes - t >= 0 && lastMinutes - t <= minutes;
  });
}

function pickMiddleStrike(strikes: string[]): string {
  if (strikes.length === 0) return "";
  const sorted = [...strikes].sort((a, b) => parseFloat(a) - parseFloat(b));
  return sorted[Math.floor(sorted.length / 2)];
}

function offsetStrikeKey(selected: string, offset: number): string {
  const parsed = parseFloat(selected);
  if (Number.isNaN(parsed)) return "";
  return String(Math.round(parsed + offset));
}

function extractErrorMessage(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (err as { response?: { data?: { message?: string; detail?: string } } }).response;
    return response?.data?.message ?? response?.data?.detail ?? "Failed to load strike chart data.";
  }
  return "Failed to load strike chart data.";
}

// Mirrors the backend's IST-based "today" resolution (see
// strike_chart_history_query_service.py's `_ist_today`), purely so the
// date picker's `max` bound matches what "today" means server-side -- this
// is NOT used to decide the weekend/holiday fallback itself, that's
// entirely server-driven.
function todayIsoInIST(): string {
  const now = new Date();
  const istMillis = now.getTime() + (5.5 * 60 + now.getTimezoneOffset()) * 60_000;
  const ist = new Date(istMillis);
  const y = ist.getFullYear();
  const m = String(ist.getMonth() + 1).padStart(2, "0");
  const d = String(ist.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatBannerDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

function StrikeStatRow({
  points,
  windowPoints,
}: {
  points: StrikeChartPoint[] | undefined;
  windowPoints: StrikeChartPoint[] | undefined;
}) {
  if (!points || points.length === 0) return null;
  const last = points[points.length - 1];
  const window = windowPoints && windowPoints.length > 0 ? windowPoints : [last];
  const windowStart = window[0];
  const fp = last.fp ?? 0;
  const fc = last.fc ?? 0;
  const fpDelta = fp - (windowStart.fp ?? 0);
  const fcDelta = fc - (windowStart.fc ?? 0);
  const fpColor = fp >= fc ? "#3fb950" : "#f85149";
  const fcColor = fc >= fp ? "#f85149" : "#3fb950";
  const fpDeltaColor = fpDelta < -15 ? "#f85149" : fpDelta > 15 ? "#3fb950" : "text.secondary";
  const fcDeltaColor = fcDelta < -15 ? "#3fb950" : fcDelta > 15 ? "#f85149" : "text.secondary";

  const cells: Array<[string, string, string]> = [
    ["FP", `${fp.toFixed(1)}L`, fpColor],
    ["FC", `${fc.toFixed(1)}L`, fcColor],
    ["FP Δ", `${fpDelta >= 0 ? "+" : ""}${fpDelta.toFixed(1)}L`, fpDeltaColor],
    ["FC Δ", `${fcDelta >= 0 ? "+" : ""}${fcDelta.toFixed(1)}L`, fcDeltaColor],
  ];

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1px", mt: 0.5 }}>
      {cells.map(([label, value, color]) => (
        <Box key={label} sx={{ textAlign: "center", py: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ display: "block", color: "text.secondary", fontSize: 9, textTransform: "uppercase" }}
          >
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, color }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function buildChartData(points: StrikeChartPoint[] | undefined) {
  const pts = points ?? [];
  const labels = pts.map((p) => p.ts);
  const fc = pts.map((p) => p.fc);
  const fp = pts.map((p) => p.fp);
  const putBig = pts.map((p) => (p.fp >= FP_BIG ? p.fp : null));
  const putSm = pts.map((p) => (p.fp >= FP_SM && p.fp < FP_BIG ? p.fp : null));
  const callBig = pts.map((p) => (p.fc >= FC_BIG ? p.fc : null));
  const callSm = pts.map((p) => (p.fc >= FC_SM && p.fc < FC_BIG ? p.fc : null));

  return {
    labels,
    datasets: [
      {
        label: "Fresh Call OI (L)",
        data: fc,
        borderColor: "#f85149",
        backgroundColor: "#f85149",
        pointRadius: 2,
        tension: 0.3,
      },
      {
        label: "Fresh Put OI (L)",
        data: fp,
        borderColor: "#3fb950",
        backgroundColor: "#3fb950",
        pointRadius: 2,
        tension: 0.3,
      },
      {
        label: `Put ≥${FP_BIG}L`,
        data: putBig,
        showLine: false,
        pointStyle: "triangle",
        pointRadius: 7,
        borderColor: "#00d68f",
        backgroundColor: "#00d68f",
      },
      {
        label: `Put ≥${FP_SM}L`,
        data: putSm,
        showLine: false,
        pointStyle: "circle",
        pointRadius: 4,
        borderColor: "#00d68f",
        backgroundColor: "rgba(0,214,143,.6)",
      },
      {
        label: `Call ≥${FC_BIG}L`,
        data: callBig,
        showLine: false,
        pointStyle: "rectRot",
        pointRadius: 6,
        borderColor: "#ff6b6b",
        backgroundColor: "#ff6b6b",
      },
      {
        label: `Call ≥${FC_SM}L`,
        data: callSm,
        showLine: false,
        pointStyle: "circle",
        pointRadius: 4,
        borderColor: "#ff6b6b",
        backgroundColor: "rgba(255,107,107,.6)",
      },
    ],
  };
}

function StrikePanel({
  title,
  points,
  timeframeMinutes,
}: {
  title: string;
  points: StrikeChartPoint[] | undefined;
  timeframeMinutes: number | null;
}) {
  const windowPoints = useMemo(() => filterByTimeframe(points, timeframeMinutes), [points, timeframeMinutes]);
  const chartData = useMemo(() => buildChartData(windowPoints), [windowPoints]);
  const hasData = (windowPoints?.length ?? 0) > 0;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <StrikeStatRow points={points} windowPoints={windowPoints} />
      <Box sx={{ height: 240, mt: 1 }}>
        {hasData ? (
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              animation: false,
              plugins: {
                legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 9 } } },
              },
              scales: {
                x: { ticks: { font: { size: 9 } } },
                y: { ticks: { font: { size: 9 } } },
              },
            }}
          />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            <Typography variant="caption" color="text.secondary">
              No data for this strike yet
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default function OIStrikeChartsPage() {
  const view = useAnalystIndexView();
  const [niftyData, setNiftyData] = useState<StrikeChartHistoryResponse | null>(null);
  const [bnData, setBnData] = useState<StrikeChartHistoryResponse | null>(null);
  const [niftyStrike, setNiftyStrike] = useState<string>("");
  const [bnStrike, setBnStrike] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const [timeframeLabel, setTimeframeLabel] = useState<string>(DEFAULT_TIMEFRAME_LABEL);
  // null = live/"today" (auto-refreshing); a specific ISO date = the
  // historical date picker below, reviewing one past session.
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const timeframeMinutes = useMemo(
    () => TIMEFRAME_OPTIONS.find((opt) => opt.label === timeframeLabel)?.minutes ?? null,
    [timeframeLabel],
  );
  const maxDate = useMemo(() => todayIsoInIST(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nf, bn] = await Promise.all([
        oiStrikeChartsService.getHistory("NIFTY", selectedDate ?? undefined),
        oiStrikeChartsService.getHistory("BANKNIFTY", selectedDate ?? undefined),
      ]);
      setNiftyData(nf);
      setBnData(bn);
      setNiftyStrike((prev) => {
        const keys = Object.keys(nf.strikes);
        if (prev && keys.includes(prev)) return prev;
        return pickMiddleStrike(keys);
      });
      setBnStrike((prev) => {
        const keys = Object.keys(bn.strikes);
        if (prev && keys.includes(prev)) return prev;
        return pickMiddleStrike(keys);
      });
      setLastLoadedAt(new Date());
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    load();
    if (selectedDate !== null) {
      // Reviewing a fixed historical date -- nothing about it will change,
      // so there's no reason to keep polling every 60s like the live view.
      return;
    }
    // TekInvest's live collector only writes a new bar every
    // SCHEDULER_INTERVAL_MINUTES (5 min in production) -- unlike
    // oi_server.py's 10s browser poll, refreshing faster than that would
    // just repeat the same data. 60s keeps the page reasonably live
    // without hammering the API.
    const id = window.setInterval(load, 60_000);
    return () => window.clearInterval(id);
  }, [load, selectedDate]);

  const niftyStrikes = useMemo(
    () => Object.keys(niftyData?.strikes ?? {}).sort((a, b) => parseFloat(a) - parseFloat(b)),
    [niftyData],
  );
  const bnStrikes = useMemo(
    () => Object.keys(bnData?.strikes ?? {}).sort((a, b) => parseFloat(a) - parseFloat(b)),
    [bnData],
  );

  const nfBelowKey = offsetStrikeKey(niftyStrike, -STEP.NIFTY);
  const nfAboveKey = offsetStrikeKey(niftyStrike, STEP.NIFTY);
  const bnBelowKey = offsetStrikeKey(bnStrike, -STEP.BANKNIFTY);
  const bnAboveKey = offsetStrikeKey(bnStrike, STEP.BANKNIFTY);

  const fallbackSource = niftyData?.is_fallback ? niftyData : bnData?.is_fallback ? bnData : null;
  const explicitNoData =
    selectedDate !== null &&
    !loading &&
    niftyData !== null &&
    bnData !== null &&
    Object.keys(niftyData.strikes).length === 0 &&
    Object.keys(bnData.strikes).length === 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Strike-Level Fresh OI Flow Terminal
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {lastLoadedAt && (
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastLoadedAt.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh now">
            <span>
              <IconButton size="small" onClick={load} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {fallbackSource && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Markets are closed. Displaying static session data from {formatBannerDate(fallbackSource.trade_date)}.
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        sx={{ mb: 2 }}
      >
        <ToggleButtonGroup
          value={timeframeLabel}
          exclusive
          size="small"
          onChange={(_event, value) => {
            if (value) setTimeframeLabel(value);
          }}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontFamily: "monospace",
              fontSize: 12,
              px: 1.5,
              py: 0.25,
              color: "text.secondary",
              borderColor: "divider",
            },
            "& .MuiToggleButton-root.Mui-selected": {
              color: "#00d68f",
              backgroundColor: "rgba(0,214,143,0.12)",
              borderColor: "#00d68f",
            },
          }}
        >
          {TIMEFRAME_OPTIONS.map((opt) => (
            <ToggleButton key={opt.label} value={opt.label}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          type="date"
          size="small"
          label="Historical date"
          value={selectedDate ?? ""}
          onChange={(e) => setSelectedDate(e.target.value || null)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: maxDate }}
          sx={{ minWidth: 170 }}
        />

        {selectedDate !== null && (
          <Chip label="Back to Live" size="small" onClick={() => setSelectedDate(null)} sx={{ cursor: "pointer" }} />
        )}
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Fresh call/put OI accumulated per strike since today's opening bar, banded around the day's
        opening spot ({niftyData?.band_limit ?? 300} pts for NIFTY, {bnData?.band_limit ?? 700} pts for
        BANKNIFTY). Bars are TekInvest's live 5-minute collector cadence — coarser than the original
        terminal's ~3.5-minute poll, same underlying fields.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {explicitNoData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No OI flow data is available for {formatBannerDate(selectedDate as string)}. Try a different
          trading date or switch back to Live.
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mb: 2 }}>
        <FormControl
          size="small"
          sx={{ minWidth: 180, display: view === "BANKNIFTY" ? "none" : undefined }}
          disabled={niftyStrikes.length === 0}
        >
          <InputLabel>Nifty Anchor Strike</InputLabel>
          <Select
            value={niftyStrikes.includes(niftyStrike) ? niftyStrike : ""}
            label="Nifty Anchor Strike"
            onChange={(e) => setNiftyStrike(e.target.value)}
          >
            {niftyStrikes.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          size="small"
          sx={{ minWidth: 180, display: view === "NIFTY" ? "none" : undefined }}
          disabled={bnStrikes.length === 0}
        >
          <InputLabel>BankNifty Anchor Strike</InputLabel>
          <Select
            value={bnStrikes.includes(bnStrike) ? bnStrike : ""}
            label="BankNifty Anchor Strike"
            onChange={(e) => setBnStrike(e.target.value)}
          >
            {bnStrikes.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading && !niftyData && !bnData ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} {...indexSectionProps(view, "NIFTY", "md")}>
            <StrikePanel
              title={`Nifty Anchor Strike${niftyStrike ? ` (${niftyStrike})` : ""}`}
              points={niftyData?.strikes[niftyStrike]}
              timeframeMinutes={timeframeMinutes}
            />
          </Grid>
          <Grid item xs={12} {...indexSectionProps(view, "BANKNIFTY", "md")}>
            <StrikePanel
              title={`BankNifty Anchor Strike${bnStrike ? ` (${bnStrike})` : ""}`}
              points={bnData?.strikes[bnStrike]}
              timeframeMinutes={timeframeMinutes}
            />
          </Grid>

          <Grid item xs={12} {...indexSectionProps(view, "NIFTY", "md")}>
            <StrikePanel
              title={`Nifty Lower Strike (-${STEP.NIFTY})${nfBelowKey ? ` (${nfBelowKey})` : ""}`}
              points={niftyData?.strikes[nfBelowKey]}
              timeframeMinutes={timeframeMinutes}
            />
          </Grid>
          <Grid item xs={12} {...indexSectionProps(view, "BANKNIFTY", "md")}>
            <StrikePanel
              title={`BankNifty Lower Strike (-${STEP.BANKNIFTY})${bnBelowKey ? ` (${bnBelowKey})` : ""}`}
              points={bnData?.strikes[bnBelowKey]}
              timeframeMinutes={timeframeMinutes}
            />
          </Grid>

          <Grid item xs={12} {...indexSectionProps(view, "NIFTY", "md")}>
            <StrikePanel
              title={`Nifty Upper Strike (+${STEP.NIFTY})${nfAboveKey ? ` (${nfAboveKey})` : ""}`}
              points={niftyData?.strikes[nfAboveKey]}
              timeframeMinutes={timeframeMinutes}
            />
          </Grid>
          <Grid item xs={12} {...indexSectionProps(view, "BANKNIFTY", "md")}>
            <StrikePanel
              title={`BankNifty Upper Strike (+${STEP.BANKNIFTY})${bnAboveKey ? ` (${bnAboveKey})` : ""}`}
              points={bnData?.strikes[bnAboveKey]}
              timeframeMinutes={timeframeMinutes}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
